/**
 * AWARE Health — Generic Country Choropleth Layer
 *
 * Replaces VietnamChoropleth — works for any country with a GeoJSON file.
 * Features:
 *  - Risk-colored heatmap per admin region
 *  - Zoom to region bounds on click (fitBounds with padding)
 *  - Two-way hover sync with sidebar (imperative setStyle — no re-renders)
 *  - Rich tooltips with calibrated rate, risk level, and alert status
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { GeoJSON, useMap } from 'react-leaflet'
import L from 'leaflet'
import type { Layer, PathOptions } from 'leaflet'
import type { ForecastRecord } from '../../types/forecast'

// ── Name normalisation maps per country ──────────────────────────────────────
// Maps GeoJSON feature Name → forecast region_name
// Needed when GeoJSON spellings differ from forecast data region_name field
const NAME_ALIASES: Record<string, Record<string, string>> = {
  VN: {
    // GeoJSON Name              → forecast region_name
    'Bac Kan':                    'Bac Can',        // GeoJSON typo vs forecast
    'TP. Ho Chi Minh':            'Ho Chi Minh',    // Official vs short name
    'Thua Thien - Hue':           'Thua Thien Hue', // Hyphen vs space
  },
  TW: {
    // GeoJSON Name  → forecast region_name  (all match directly after our transform)
  },
  NP: {
    // GeoJSON Name  → forecast region_name  (all match directly after PCODE mapping)
  },
}

// ── Style config per risk + state ────────────────────────────────────────────
const RISK_STYLES: Record<string, {
  normal:   PathOptions
  hover:    PathOptions
  selected: PathOptions
  both:     PathOptions
}> = {
  low: {
    normal:   { fillColor: 'rgba(16,185,129,0.22)',  color: 'rgba(16,185,129,0.5)',  weight: 0.8, fillOpacity: 1, opacity: 1 },
    hover:    { fillColor: 'rgba(16,185,129,0.55)',  color: '#10b981',               weight: 2.5, fillOpacity: 1, opacity: 1 },
    selected: { fillColor: 'rgba(16,185,129,0.55)',  color: '#ffffff',               weight: 2.5, fillOpacity: 1, opacity: 1 },
    both:     { fillColor: 'rgba(16,185,129,0.70)',  color: '#ffffff',               weight: 3,   fillOpacity: 1, opacity: 1 },
  },
  medium: {
    normal:   { fillColor: 'rgba(245,158,11,0.25)',  color: 'rgba(245,158,11,0.5)', weight: 0.8, fillOpacity: 1, opacity: 1 },
    hover:    { fillColor: 'rgba(245,158,11,0.60)',  color: '#f59e0b',              weight: 2.5, fillOpacity: 1, opacity: 1 },
    selected: { fillColor: 'rgba(245,158,11,0.60)',  color: '#ffffff',              weight: 2.5, fillOpacity: 1, opacity: 1 },
    both:     { fillColor: 'rgba(245,158,11,0.75)',  color: '#ffffff',              weight: 3,   fillOpacity: 1, opacity: 1 },
  },
  high: {
    normal:   { fillColor: 'rgba(239,68,68,0.28)',   color: 'rgba(239,68,68,0.5)',  weight: 0.8, fillOpacity: 1, opacity: 1 },
    hover:    { fillColor: 'rgba(239,68,68,0.62)',   color: '#ef4444',              weight: 2.5, fillOpacity: 1, opacity: 1 },
    selected: { fillColor: 'rgba(239,68,68,0.62)',   color: '#ffffff',              weight: 2.5, fillOpacity: 1, opacity: 1 },
    both:     { fillColor: 'rgba(239,68,68,0.78)',   color: '#ffffff',              weight: 3,   fillOpacity: 1, opacity: 1 },
  },
  nodata: {
    normal:   { fillColor: 'rgba(15,26,46,0.75)',    color: 'rgba(255,255,255,0.07)', weight: 0.5, fillOpacity: 1, opacity: 1 },
    hover:    { fillColor: 'rgba(30,50,80,0.80)',    color: 'rgba(255,255,255,0.30)', weight: 1.5, fillOpacity: 1, opacity: 1 },
    selected: { fillColor: 'rgba(30,50,80,0.80)',    color: '#ffffff',                weight: 1.5, fillOpacity: 1, opacity: 1 },
    both:     { fillColor: 'rgba(40,65,100,0.80)',   color: '#ffffff',                weight: 2,   fillOpacity: 1, opacity: 1 },
  },
}

// ── Country config ────────────────────────────────────────────────────────────
interface CountryConfig {
  geojsonPath: string
  center: [number, number]
  zoom: number
}

const COUNTRY_CONFIG: Record<string, CountryConfig> = {
  VN: { geojsonPath: '/geodata/vn-provinces.geojson', center: [16.5, 106.0], zoom: 6 },
  TW: { geojsonPath: '/geodata/tw-counties.geojson',  center: [23.7, 121.0], zoom: 8 },
  NP: { geojsonPath: '/geodata/np-provinces.geojson', center: [28.3, 84.1],  zoom: 7 },
}

interface GeoJsonFeature {
  type: 'Feature'
  properties: { Name: string; id?: string }
  geometry: unknown
}

interface ProvinceData {
  regionId: string
  record: ForecastRecord
}

interface CountryChoroplethProps {
  countryCode: string
  forecastByRegionName: Map<string, ProvinceData>
  selectedRegionId: string | null
  hoveredRegionId: string | null
  onProvinceClick: (regionId: string) => void
  onProvinceHover: (regionId: string | null) => void
}

interface LayerEntry {
  layer: L.Path
  match: ProvinceData | null
  geoName: string
}

export default function CountryChoropleth({
  countryCode,
  forecastByRegionName,
  selectedRegionId,
  hoveredRegionId,
  onProvinceClick,
  onProvinceHover,
}: CountryChoroplethProps) {
  const [geoData, setGeoData] = useState<{ type: string; features: GeoJsonFeature[] } | null>(null)
  const map = useMap()
  const aliases = NAME_ALIASES[countryCode] ?? {}
  const config = COUNTRY_CONFIG[countryCode]

  const layersByRegionId = useRef(new Map<string, LayerEntry>())
  const layersByGeoName  = useRef(new Map<string, LayerEntry>())

  // Fly to country center on mount
  useEffect(() => {
    if (config) {
      map.flyTo(config.center, config.zoom, { duration: 1.0 })
    }
    // Clear layer caches when country changes
    layersByRegionId.current.clear()
    layersByGeoName.current.clear()
  }, [countryCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch GeoJSON
  useEffect(() => {
    if (!config) return
    setGeoData(null)
    fetch(config.geojsonPath)
      .then((r) => r.json())
      .then(setGeoData)
      .catch((e) => console.error('[CountryChoropleth] GeoJSON load failed:', e))
  }, [config?.geojsonPath]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Style helpers ──────────────────────────────────────────────────────────
  function getStyle(entry: LayerEntry, forceHover = false, forceSelect = false): PathOptions {
    const riskKey = entry.match?.record.risk_category ?? 'nodata'
    const styles  = RISK_STYLES[riskKey] ?? RISK_STYLES.nodata
    if (forceHover && forceSelect) return styles.both
    if (forceSelect)               return styles.selected
    if (forceHover)                return styles.hover
    return styles.normal
  }

  function applyStyle(entry: LayerEntry, isHovered: boolean, isSelected: boolean) {
    entry.layer.setStyle(getStyle(entry, isHovered, isSelected))
    if (isHovered || isSelected) entry.layer.bringToFront()
  }

  // ── React to hoveredRegionId changes (sidebar → map) ───────────────────────
  useEffect(() => {
    layersByGeoName.current.forEach((entry) => {
      const rid = entry.match?.regionId ?? null
      applyStyle(entry, rid !== null && rid === hoveredRegionId, rid !== null && rid === selectedRegionId)
    })
  }, [hoveredRegionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── React to selectedRegionId changes ──────────────────────────────────────
  useEffect(() => {
    layersByGeoName.current.forEach((entry) => {
      const rid = entry.match?.regionId ?? null
      applyStyle(entry, rid !== null && rid === hoveredRegionId, rid !== null && rid === selectedRegionId)
    })
  }, [selectedRegionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fly to region bounds ───────────────────────────────────────────────────
  function flyToRegion(layer: L.Path) {
    try {
      const bounds = (layer as unknown as L.Polygon).getBounds()
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 11, animate: true, duration: 0.6 })
      }
    } catch (_) { /* ignore */ }
  }

  // ── Initial style ──────────────────────────────────────────────────────────
  const style = useCallback(
    (feature?: GeoJsonFeature): PathOptions => {
      if (!feature) return {}
      const norm    = aliases[feature.properties.Name] ?? feature.properties.Name
      const match   = forecastByRegionName.get(norm) ?? null
      const riskKey = match?.record.risk_category ?? 'nodata'
      return RISK_STYLES[riskKey].normal
    },
    [forecastByRegionName, countryCode], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ── Bind events ────────────────────────────────────────────────────────────
  const onEachFeature = useCallback(
    (feature: GeoJsonFeature, layer: Layer) => {
      const geoName = feature.properties.Name
      const norm    = aliases[geoName] ?? geoName
      const match   = forecastByRegionName.get(norm) ?? null
      const entry: LayerEntry = { layer: layer as L.Path, match, geoName }

      layersByGeoName.current.set(geoName, entry)
      if (match) layersByRegionId.current.set(match.regionId, entry)

      layer.on({
        mouseover: () => {
          applyStyle(entry, true, match?.regionId === selectedRegionId)
          onProvinceHover(match?.regionId ?? null)

          const riskKey = match?.record.risk_category ?? 'nodata'
          const riskColors: Record<string, string> = {
            low: '#10b981', medium: '#f59e0b', high: '#ef4444', nodata: '#475569',
          }
          const c = riskColors[riskKey]
          const tooltipContent = match
            ? `<div style="font-family:Inter,sans-serif;min-width:140px">
                <div style="font-weight:700;font-size:13px;margin-bottom:5px">${geoName}</div>
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${c};flex-shrink:0"></span>
                  <span style="font-size:11px;font-weight:600;color:${c};text-transform:uppercase;letter-spacing:0.05em">${riskKey} risk</span>
                </div>
                <div style="font-size:11px;opacity:0.8">Rate:&nbsp;
                  <span style="font-family:'JetBrains Mono',monospace;font-weight:500;opacity:1">
                    ${match.record.forecast_calibrated_median.toFixed(4)}
                  </span>
                  <span style="opacity:0.6"> / 100k</span>
                </div>
                <div style="font-size:10px;opacity:0.8;margin-top:2px">Alert: ${match.record.alert_level}</div>
                <div style="font-size:9px;color:#0d9488;margin-top:4px">Click to zoom &amp; view forecast</div>
              </div>`
            : `<div style="font-family:Inter,sans-serif">
                <div style="font-weight:700;font-size:13px;margin-bottom:3px">${geoName}</div>
                <div style="font-size:10px;opacity:0.7">No forecast data available</div>
              </div>`

          ;(layer as L.Path).bindTooltip(tooltipContent, {
            sticky: true, opacity: 1, className: 'aware-tooltip',
          }).openTooltip()
        },

        mouseout: () => {
          applyStyle(entry, false, match?.regionId === selectedRegionId)
          onProvinceHover(null)
          ;(layer as L.Path).closeTooltip()
        },

        click: () => {
          flyToRegion(layer as L.Path)
          if (match) onProvinceClick(match.regionId)
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forecastByRegionName, countryCode],
  )

  if (!geoData) return null

  return (
    <GeoJSON
      key={`${countryCode}-choropleth`}
      data={geoData as unknown as Parameters<typeof GeoJSON>[0]['data']}
      style={style as unknown as Parameters<typeof GeoJSON>[0]['style']}
      onEachFeature={onEachFeature as unknown as Parameters<typeof GeoJSON>[0]['onEachFeature']}
    />
  )
}
